// web/src/screens/admin/tasks_list.ts — Plan 11-06 Task 3.
//
// Renders the Tasks panel inside cards_edit.ts. Mirrors the cards_list
// pattern (renderCardsPanel) so the API surface is uniform. Plan 11-07
// keeps this signature stable when it adds Step/Action navigation.
//
// All API-supplied text via textContent (T-11-09a).

import { adminGet } from "../../api/admin.js";
import type { AdminTaskDTO } from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function renderTasksPanel(
  parent: HTMLElement,
  cardID: string,
  gameShortCode: string,
  cardShortCode: string,
): void {
  const wrap = document.createElement("div");
  wrap.setAttribute("style", "background: white; padding: 12px; border-radius: 6px; border: 1px solid #e5e7eb;");
  parent.appendChild(wrap);

  const toolbar = document.createElement("div");
  toolbar.setAttribute("style", "display: flex; gap: 8px; align-items: center; margin-bottom: 8px;");
  const newBtn = document.createElement("button");
  newBtn.type = "button";
  newBtn.textContent = "+ New Task";
  newBtn.setAttribute("style", "padding: 6px 12px; background: #2563eb; color: white; border: 0; border-radius: 4px; cursor: pointer; font-size: 13px;");
  toolbar.appendChild(newBtn);
  wrap.appendChild(toolbar);

  const status = document.createElement("p");
  status.textContent = "Loading tasks…";
  status.setAttribute("style", "color: #6b7280; font-size: 13px; margin: 4px 0;");
  wrap.appendChild(status);

  const ul = document.createElement("ul");
  ul.setAttribute("style", "list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px;");
  wrap.appendChild(ul);

  function rowFor(t: AdminTaskDTO): HTMLLIElement {
    const li = document.createElement("li");
    li.setAttribute("style", "display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px;");
    const a = document.createElement("a");
    a.textContent = t.short_code ?? "(no short_code)";
    const href = `/admin/games/${encodeURIComponent(gameShortCode)}/cards/${encodeURIComponent(cardShortCode)}/tasks/${encodeURIComponent(t.short_code ?? "")}`;
    a.setAttribute("href", href);
    a.setAttribute("style", "color: #1d4ed8; text-decoration: none; font-weight: 500; flex: 1;");
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const sc = t.short_code;
      if (!sc) return;
      navigateAdmin(href);
    });
    li.appendChild(a);
    if (t.image_path) {
      const tag = document.createElement("span");
      tag.textContent = "img";
      tag.setAttribute("style", "padding: 1px 6px; border-radius: 999px; background: #dbeafe; color: #1e3a8a; font-size: 11px;");
      li.appendChild(tag);
    }
    if (t.correct_answer) {
      const ans = document.createElement("span");
      ans.textContent = t.correct_answer;
      ans.setAttribute("style", "font-size: 12px; color: #6b7280;");
      li.appendChild(ans);
    }
    return li;
  }

  async function load(): Promise<void> {
    try {
      const tasks = await adminGet<AdminTaskDTO[]>(
        `/api/admin/cards/${encodeURIComponent(cardID)}/tasks`,
      );
      ul.replaceChildren();
      if (tasks.length === 0) {
        status.textContent = "No tasks yet — create one with “+ New Task”.";
        return;
      }
      status.textContent = `${tasks.length} task${tasks.length === 1 ? "" : "s"}.`;
      for (const t of tasks) ul.appendChild(rowFor(t));
    } catch (err) {
      status.textContent = `Failed to load tasks: ${(err as Error).message}`;
    }
  }

  function onNew(): void {
    navigateAdmin(`/admin/games/${encodeURIComponent(gameShortCode)}/cards/${encodeURIComponent(cardShortCode)}/tasks/new`);
  }

  newBtn.addEventListener("click", onNew);
  void load();
}
